<template>
  <!-- Main Content -->
  <div class="main-div">
    <div class="my-12 mx-auto text-center">
      <h1>Powerful Tools For Your Business</h1>
      <p class="text-h6">Pay for what you use</p>
    </div>
    <v-card class="pa-12 mx-auto signup-card">
      <v-banner color="error" dark class="mb-6" v-if="errorMessage">
        <v-avatar slot="icon" size="40">
          <v-icon>mdi-alert-circle</v-icon>
        </v-avatar>
        {{ errorMessage }}
      </v-banner>
      <v-sheet class="mx-auto text-center">
        <h3>Sign up</h3>
        <h4 class="subhead">You'll be up in running in minutes</h4>
      </v-sheet>
      <v-stepper v-model="step" class="elevation-0">
        <v-stepper-header>
          <v-stepper-step
            :complete="step > 1"
            editable
            edit-icon="$complete"
            step="1"
            >Contact</v-stepper-step
          >
          <v-divider></v-divider>
          <v-stepper-step
            :complete="step > 2"
            editable
            edit-icon="$complete"
            step="2"
            >Terms</v-stepper-step
          >
          <v-divider></v-divider>
          <v-stepper-step step="3">Checkout</v-stepper-step>
        </v-stepper-header>
        <v-stepper-items>
          <v-stepper-content step="1">
            <v-form ref="step1form" v-model="valid" class="mt-12">
              <v-row>
                <v-col cols="12" sm="12" class="py-0 text-subtitle-1 mb-10">
                  First, provide us with your contact info.
                </v-col>
                <v-col cols="12" sm="12" class="py-1">
                  <v-text-field
                    id="company-name"
                    label="Company Name"
                    placeholder=" "
                    v-model="companyNameValue"
                    class="py-1"
                    required
                    :rules="[rules.required]"
                    v-on:keyup.enter="onNext1"
                    @input="cacheData"
                    autocomplete="none"
                    autofocus
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="6" class="py-1">
                  <v-text-field
                    id="first-name"
                    label="First Name"
                    v-model="firstNameValue"
                    placeholder=" "
                    class="py-1"
                    required
                    :rules="[rules.required]"
                    v-on:keyup.enter="onNext1"
                    @input="cacheData"
                    autocomplete="none"
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="6" class="py-1">
                  <v-text-field
                    id="last-name"
                    label="Last Name"
                    v-model="lastNameValue"
                    placeholder=" "
                    class="py-1"
                    required
                    :rules="[rules.required]"
                    v-on:keyup.enter="onNext1"
                    @input="cacheData"
                    autocomplete="none"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" class="py-1">
                  <v-text-field
                    id="phone-number"
                    ref="phone-number"
                    label="Phone Number"
                    v-model="phoneNumberValue"
                    placeholder=" "
                    class="py-1"
                    required
                    :rules="[rules.required, rules.phone]"
                    v-mask="masks.phone"
                    v-on:keyup.enter="onNext1"
                    @input="cacheData"
                    autocomplete="off"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" class="py-1">
                  <v-text-field
                    id="contact-info-email-address"
                    ref="contact-info-email-address"
                    label="Email Address"
                    v-model="emailAddressValue"
                    placeholder=" "
                    class="py-1"
                    required
                    :rules="[rules.email]"
                    v-mask="masks.nomask"
                    v-on:keyup.enter="onNext1"
                    @input="cacheData"
                    autocomplete="off"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary tile" @click="onNext1"
                  >&nbsp;&nbsp;&nbsp;Next&nbsp;&nbsp;&nbsp;</v-btn
                >
              </v-card-actions>
            </v-form>
          </v-stepper-content>

          <v-stepper-content step="2">
            <v-form ref="step2form" v-model="valid" class="mt-12">
              <v-row>
                <v-col cols="12" sm="12" class="py-1 mb-10">
                  You selected the <strong>{{ planName }}</strong> plan. Now set
                  your <strong>starting balance</strong> for expected monthly
                  usage.
                </v-col>
                <v-col cols="12" sm="12" class="py-1">
                  <v-select
                    v-model="subscription"
                    required
                    :rules="[rules.required]"
                    :items="plans"
                    item-text="text"
                    item-value="value"
                    label="Change Plan"
                    @change="cacheData"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="fundAmount"
                    class="py-1"
                    dense
                    required
                    :rules="[rules.required]"
                    :items="fundAmounts"
                    item-text="text"
                    item-value="value"
                    label="Starting Balance"
                    @change="cacheData"
                  >
                  </v-select>
                </v-col>
                <v-col cols="12" class="py-1">
                  <v-card class="px-4 py-2 my-4 terms-box elevation-0">
                    <v-checkbox
                      v-model="acceptTerms"
                      required
                      :rules="[rules.required]"
                    >
                      <template v-slot:label>
                        <div style="color: #333">
                          I agree to
                          <strong><span class="brand">daba</span>'s</strong>
                          <a
                            class="accept-link"
                            target="_blank"
                            href="https://www.dabaone.com/legal/terms/"
                            @click.stop
                          >
                            Terms of Service
                          </a>
                          and
                          <a
                            class="accept-link"
                            target="_blank"
                            href="https://www.dabaone.com/legal/privacy/"
                            @click.stop
                          >
                            Privacy Policy.</a
                          >
                        </div>
                      </template>
                    </v-checkbox>
                  </v-card>
                </v-col>
              </v-row>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary tile" @click="onNext2"
                  >&nbsp;&nbsp;&nbsp;Next&nbsp;&nbsp;&nbsp;</v-btn
                >
              </v-card-actions>
            </v-form>
          </v-stepper-content>

          <v-stepper-content step="3"></v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card>
  </div>
</template>

<script>
import { VALIDATION_RULES } from "../helpers/ValidationRules";
import { STATES } from "../helpers/StaticLists";
import { CentsToDollars } from "../helpers/moneyFormatter";
import { MASKS } from "../helpers/Masks";
import {
  setWithExpiry,
  getWithExpiry,
} from "../helpers/localStorageExpiration";
import { mask } from "vue-the-mask";
import {
  SUBSCRIPTION_PLANS,
  MARKETING_WEBSITE_PLAN_URL,
  API_BASE_URL,
  STRIPE_PUBLIC_API_KEY,
} from "../globalVars";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
const stripePromise = loadStripe(STRIPE_PUBLIC_API_KEY);

export default {
  name: "SignUp",
  // components: {
  //   CustomerRightRail
  // }
  directives: {
    mask,
  },
  data: () => ({
    step: 1,
    errorMessage: null,
    valid: true,
    sessionId: null,
    subscription: null,
    promoCode: null,
    fundAmount: 4000,
    planNames: {
      daba_basic_sub_mon: "daba Call Basic (monthly billing)",
      daba_basic_sub_yr: "daba Call Basic (annual billing)",
      daba_stand_sub_mon: "daba Call Standard (monthly billing)",
      daba_stand_sub_yr: "daba Call Standard (annual billing)",
      daba_pro_sub_mon: "daba Call Pro (monthly billing)",
      daba_pro_sub_yr: "daba Call Pro (annual billing)",
    },

    companyNameValue: null,
    firstNameValue: null,
    lastNameValue: null,
    sourceSelected: null,
    otherSourceValue: null,
    phoneNumberValue: null,
    emailAddressValue: null,
    acceptTerms: false,
    // addressTypeValue: "main",
    // address1Value: null,
    // address2Value: null,
    // cityValue: null,
    // stateValue: null,
    // postalCodeValue: null,
    phoneLabels: [
      { id: "mobile", name: "Mobile" },
      { id: "home", name: "Home" },
      { id: "work", name: "Work" },
      { id: "other", name: "Other" },
    ],
    addressTypes: [
      { id: "main", name: "Main" },
      { id: "home", name: "Home" },
      { id: "office", name: "Office" },
      { id: "bill", name: "Billing" },
      { id: "ship", name: "Shipping" },
    ],

    states: STATES,
    masks: MASKS,
    rules: VALIDATION_RULES,
  }),
  created() {
    let oldSignupSessionData = this.getWithExpiry("signup-data");
    if (oldSignupSessionData && JSON.parse(oldSignupSessionData)) {
      oldSignupSessionData = JSON.parse(oldSignupSessionData);
    }
    let tempData = oldSignupSessionData ? oldSignupSessionData.data : null;
    if (tempData) {
      this.step = tempData.step;
      this.companyNameValue = tempData.companyNameValue;
      this.firstNameValue = tempData.firstNameValue;
      this.lastNameValue = tempData.lastNameValue;
      this.emailAddressValue = tempData.emailAddressValue;
      this.phoneNumberValue = tempData.phoneNumberValue;
      this.subscription = tempData.subscription;
      this.fundAmount = tempData.fundAmount;
      this.promoCode = tempData.promoCode;
    }

    let tempSession = oldSignupSessionData
      ? oldSignupSessionData.session
      : null;
    if (!tempSession) {
      tempSession = this.$uuidv4();
    }
    let newSignupSessionData = {
      session: tempSession,
      data: tempData,
    };

    this.setWithExpiry(
      "signup-data",
      JSON.stringify(newSignupSessionData),
      1000 * 60 * 60 * 24 * 7
    );
    this.sessionId = tempSession;

    this.promoCode = this.$route.query.promo;

    const plan = this.$route.query.plan;
    if (SUBSCRIPTION_PLANS.includes(plan)) {
      this.subscription = plan;
    } else {
      window.location.replace(MARKETING_WEBSITE_PLAN_URL);
    }
  },
  computed: {
    plans() {
      let planNames = this.planNames;
      return Object.keys(planNames).map(function (val) {
        return {
          value: val,
          text: planNames[val],
        };
      });
    },
    planName() {
      const plansNames = this.planNames;
      return plansNames[this.subscription]
        ? plansNames[this.subscription]
        : "unknown";
    },
    fundAmounts() {
      const items = [
        500,
        2000,
        4000,
        6000,
        8000,
        10000,
        15000,
        20000,
        25000,
        50000,
      ];
      return items.map((val) => {
        return { value: val, text: CentsToDollars(val) };
      });
    },
  },
  methods: {
    getWithExpiry: getWithExpiry,
    setWithExpiry: setWithExpiry,
    async onNext1() {
      if (this.$refs.step1form.validate()) {
        this.step = 2;
        await this.sendCustomerData();
      }
      let vueThis = this;
      this.$nextTick(function () {
        vueThis.cacheData();
      });
    },
    async onNext2() {
      this.cacheData();
      if (this.$refs.step2form.validate()) {
        await this.sendCheckoutData();
      }
    },
    cacheData() {
      this.errorMessage = null;
      const tempData = {
        step: this.step,
        companyNameValue: this.companyNameValue,
        firstNameValue: this.firstNameValue,
        lastNameValue: this.lastNameValue,
        emailAddressValue: this.emailAddressValue,
        phoneNumberValue: this.phoneNumberValue,
        subscription: this.subscription,
        fundAmount: this.fundAmount,
      };
      let newSignupSessionData = {
        session: this.sessionId,
        data: tempData,
      };
      this.setWithExpiry(
        "signup-data",
        JSON.stringify(newSignupSessionData),
        1000 * 60 * 60 * 24 * 7
      );
    },
    async sendCustomerData() {
      let response = await axios({
        method: "post",
        url: API_BASE_URL + `/account-signup`,
        data: {
          sessionId: this.sessionId,
          name: this.companyNameValue,
          adminFirstName: this.firstNameValue,
          adminLastName: this.lastNameValue,
          adminEmail: this.emailAddressValue,
          adminMobilePphoneNnumber: this.phoneNumberValue,
          subscriptionCode: this.subscription,
          couponCode: this.promoCode,
        },
      });
    },
    async sendCheckoutData() {
      let vueThis = this;
      try {
        let response = await axios({
          method: "post",
          url: API_BASE_URL + `/account-signup-checkout`,
          data: {
            sessionId: this.sessionId,
            name: this.companyNameValue,
            adminFirstName: this.firstNameValue,
            adminLastName: this.lastNameValue,
            adminEmail: this.emailAddressValue,
            adminMobilePphoneNnumber: this.phoneNumberValue,
            subscriptionCode: this.subscription,
            startingBalance: this.fundAmount,
            couponCode: this.promoCode,
          },
        });
        const stripe = await stripePromise;
        const result = await stripe.redirectToCheckout({
          sessionId: response.data.id,
        });
      } catch (error) {
        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          console.error("checkout error" + JSON.stringify(error));
          vueThis.errorMessage = error.response.data.error;
        } else {
          const msg =
            "There was an issue contacting our payment provide. Please try again later.";
          vueThis.errorMessage = msg;
          vueThis.$honeybadger.notify(new Error(msg));
        }
      }
    },
  },
  watch: {},
};
</script>
<style lang="stylus" scoped>
.signup-card {
  max-width: 560px;
}

.subhead {
  color: #555;
  font-weight: 500;
}

.terms-box {
  background-color: rgba(223, 228, 237, 0.3);
}

.accept-link {
  text-decoration: none;
}
</style>
